import { useState, useEffect, useCallback } from 'react';
import { getUserOrganizations } from '@/lib/firebase/queries/organizations/getUserOrganizations';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { Organization } from '@/types/organization';

// Special organization for general audience
const GENERAL_AUDIENCE_ORG: Organization = {
  id: 'general',
  name: 'General Audience', // This will be translated in the UI
  description: 'Create content for all platform users',
  createdAt: new Date(),
  updatedAt: new Date()
};

export function useInstructorOrganizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization>(GENERAL_AUDIENCE_ORG);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  const fetchInstructorOrganizations = useCallback(async () => {
    if (!currentUser) {
      setOrganizations([GENERAL_AUDIENCE_ORG]);
      setSelectedOrganization(GENERAL_AUDIENCE_ORG);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 Fetching instructor organizations for user:', currentUser.uid);
      
      // Get all organizations where user is an instructor
      const instructorOrgs = await getUserOrganizations(currentUser.uid, 'instructor');
      console.log('👩‍🏫 Found', instructorOrgs.length, 'instructor organizations');
      
      // Always include general audience as first option
      const allOptions = [GENERAL_AUDIENCE_ORG, ...instructorOrgs];
      setOrganizations(allOptions);
      
      // Keep current selection if still valid, otherwise default to general audience
      if (selectedOrganization && selectedOrganization.id !== 'general') {
        const stillExists = instructorOrgs.find(org => org.id === selectedOrganization.id);
        if (!stillExists) {
          setSelectedOrganization(GENERAL_AUDIENCE_ORG);
          console.log('🔄 Switched back to general audience - organization no longer available');
        }
      }
      
    } catch (error) {
      console.error('Error fetching instructor organizations:', error);
      toast.error('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  }, [currentUser, selectedOrganization]);

  const switchOrganization = useCallback((organization: Organization) => {
    console.log('🔄 Switching to organization:', organization.name);
    setSelectedOrganization(organization);
    if (organization.id === 'general') {
      toast.success('Switched to General Audience mode');
    } else {
      toast.success(`Switched to ${organization.name}`);
    }
  }, []);

  useEffect(() => {
    fetchInstructorOrganizations();
  }, [fetchInstructorOrganizations]);

  return {
    organizations,
    selectedOrganization,
    loading,
    switchOrganization,
    refreshOrganizations: fetchInstructorOrganizations,
    hasMultipleOptions: organizations.length > 1,
    isGeneralMode: selectedOrganization.id === 'general',
    contentMode: selectedOrganization.id === 'general' ? 'general' as const : 'organization' as const
  };
}